import {
  ActionActor,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { ScheduleChannelType } from '@dadohr/shared/domainTypes/schedules';
import { Chip } from '@material-ui/core';
import { FilterOptionsState } from '@material-ui/lab';
import { assertNever } from 'assert-never';
import { capitalize, flatten } from 'lodash';
import React, { useMemo } from 'react';

import {
  AutocompleteMulti,
  AutocompleteSingle
} from '../../../../../components/Autocomplete';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import { channelToString } from '../../../../../services/channels';
import { getSelectableSupportingActors } from '../../../../../services/experiences/actorUsage';
import { toSearchTermRegexp } from '../../../../../services/user';
import { TaskBuilderContext } from '../TaskBuilder/types';

export const actionActorToString = (
  o: ActionActor,
  supportingActors: TaskBuilderContext['supportingActors'],
  t: UnsafeTFunction
) => {
  if (o.type === 'other') {
    return o.email;
  }
  if (o.type === 'actor') {
    const actor = supportingActors[o.key];
    const actorFragment = actor?.name || capitalize(o.key);
    return `${actorFragment} (${channelToString(
      {
        type: ScheduleChannelType.EMAIL,
        emailFrom: o.emailField
      },
      t
    )})`;
  }

  return assertNever(o);
};

export const ActionActorSelectorMulti = ({
  value,
  onChange,
  expTpl,
  fullWidth,
  label,
  error,
  helperText,
  onBlur,
  filterOptions
}: {
  value: ActionActor[];
  onChange: (nextValue: ActionActor[]) => void;
  expTpl: TaskBuilderContext;
  fullWidth?: boolean;
  label?: React.ReactNode;
  error?: boolean;
  helperText?: React.ReactNode;
  onBlur?: () => void;
  filterOptions?: (
    options: ActionActor[],
    state: FilterOptionsState<ActionActor>
  ) => ActionActor[];
}) => {
  const { t } = useUnsafeTranslation();
  const { supportingActors } = expTpl;
  const options = useMemo(() => {
    // also take the current values into account, so that
    // we get better selection matching
    const emailFields = ['personalEmail', 'email'];
    return flatten([
      emailFields.map((emailField) => ({
        type: 'actor' as const,
        key: PARTICIPANT_ACTOR_KEY,
        emailField,
        optional: false
      })),
      // A channel actor has no email to resolve any of these fields from.
      ...getSelectableSupportingActors(supportingActors, {
        includeChannelActors: false
      }).map((actor) =>
        emailFields.map((emailField) => ({
          type: 'actor' as const,
          key: actor.key,
          emailField,
          optional: false
        }))
      )
    ]);
  }, [supportingActors]);

  return (
    <AutocompleteMulti
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      onBlur={onBlur}
      renderOption={(o) => actionActorToString(o, supportingActors, t)}
      filterOptions={(options, params) => {
        const filteredOptions = filterOptions
          ? filterOptions(options, params)
          : options;

        if (!params.inputValue) {
          return filteredOptions;
        }
        const re = toSearchTermRegexp(params.inputValue);
        return filteredOptions.filter((o) => {
          return re.exec(actionActorToString(o, supportingActors, t));
        });
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            variant="default"
            size="small"
            label={actionActorToString(option, supportingActors, t)}
            {...getTagProps({ index })}
          />
        ))
      }
      fullWidth={fullWidth}
      error={error}
      helperText={helperText}
    />
  );
};

export const ActionActorSelectorSingle = ({
  value,
  onChange,
  expTpl,
  fullWidth,
  label
}: {
  value: ActionActor | null;
  onChange: (nextValue: ActionActor | null) => void;
  expTpl: TaskBuilderContext;
  fullWidth?: boolean;
  label?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  const { supportingActors } = expTpl;
  const options = useMemo(() => {
    // also take the current values into account, so that
    // we get better selection matching
    const emailFields = ['personalEmail', 'email'];
    return flatten([
      emailFields.map((emailField) => ({
        type: 'actor' as const,
        key: PARTICIPANT_ACTOR_KEY,
        emailField,
        optional: false
      })),
      // A channel actor has no email to resolve any of these fields from.
      ...getSelectableSupportingActors(supportingActors, {
        includeChannelActors: false
      }).map((actor) =>
        emailFields.map((emailField) => ({
          type: 'actor' as const,
          key: actor.key,
          emailField,
          optional: false
        }))
      )
    ]);
  }, [supportingActors]);

  return (
    <AutocompleteSingle
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      renderOption={(o) => actionActorToString(o, supportingActors, t)}
      filterOptions={(options, params) => {
        if (!params.inputValue) {
          return options;
        }
        const re = toSearchTermRegexp(params.inputValue);
        return options.filter((o) => {
          return re.exec(actionActorToString(o, supportingActors, t));
        });
      }}
      getOptionLabel={(o) => actionActorToString(o, supportingActors, t)}
      fullWidth={fullWidth}
    />
  );
};
